/**
 * useEliasWorkspace — fetches the Elias workspace row by slug and
 * exposes its branding/feature settings to the shell.
 *
 * Mirrors apps/web/src/ministry/hooks/useMinistryWorkspace.ts.
 * Workspace is the data-driven source of truth for edition behaviour;
 * the Fly app per-edition only gives traffic isolation.
 */

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useEliasEdition } from "./useEliasEdition";

export interface EliasBranding {
  name: string;
  short_name: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
}

export interface EliasFeatures {
  chat_enabled: boolean;
  document_upload_prominent: boolean;
  /** Whether the integrations connector tile rail surfaces on the chat landing. */
  integrations_landing: boolean;
}

export interface EliasWorkspace {
  id: string;
  slug: string;
  name: string;
  edition: string;
  branding: EliasBranding;
  features: EliasFeatures;
  language: string;
}

export function useEliasWorkspace() {
  const { workspaceSlug, edition } = useEliasEdition();
  const isElias = edition === "individual-elias";
  const [workspace, setWorkspace] = useState<EliasWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isElias) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: queryError } = await supabase
        .from("workspaces")
        .select("id, slug, name, settings")
        .eq("slug", workspaceSlug)
        .maybeSingle();

      if (cancelled) return;

      if (queryError) {
        setError(queryError.message);
        setLoading(false);
        return;
      }
      if (!data) {
        setError("Elias workspace not visible — check membership");
        setLoading(false);
        return;
      }

      const settings = (data.settings ?? {}) as Record<string, unknown>;
      setWorkspace({
        id: data.id,
        slug: data.slug,
        name: data.name,
        edition: (settings.edition as string) ?? "individual-elias",
        branding: (settings.branding as EliasBranding) ?? {
          // TODO(elias-branding): replace with Elias's chosen brand tokens.
          name: data.name,
          short_name: "ELIAS",
          logo_url: null,
          primary_color: "#111111",
          accent_color: "#6366f1",
        },
        features: (settings.features as EliasFeatures) ?? {
          chat_enabled: true,
          document_upload_prominent: true,
          integrations_landing: true,
        },
        language: (settings.language as string) ?? "en",
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isElias, workspaceSlug]);

  return { workspace, loading, error };
}
